import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';

import {
  SearchBox,
  NextSearchBoxContent,
  LoadingSpinner,
  UserInfo,
  Flexbox,
  ToolButton
} from '@ibinder/ui-common-core';
import { search, plus, check } from '@ibinder/common-icons';
import { debounce } from '@ibinder/common-utils';

import { getAppSetting } from '../../../../../../../Shared/ApplicationSettings';

import {
  AddUsersHeader,
  UserChipList,
  SelectedUserChip,
  LoadingWrapper,
  UserChipListHeader,
  NoUsersAddedInfo,
  UserWrapper,
  NoResultsFound
} from './Users.styles';
import { GET_USERS } from '../../../../api/queries';
import { usersApiRestClient } from '../../../../api/rest.configuration';
import { AddUsersProps, UserType } from './Users.types';

type UserSearchResultsItem = UserType & { isAlreadyAdded: boolean };

export const AddUsersView = ({ existingUsers, updateData }: AddUsersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [addedUsers, setAddedUsers] = useState<UserType[]>([]);

  const { data, loading } = useQuery(GET_USERS, {
    client: usersApiRestClient,
    fetchPolicy: 'no-cache',
    variables: {
      filterQuery: searchTerm
    },
    skip: !searchTerm.length
  });

  const searchResults: UserSearchResultsItem[] =
    data?.getUsers.data.map((u) => ({
      ...u,
      isAlreadyAdded: existingUsers.some((x) => x.id === u.id)
    })) ?? [];

  const triggerSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, parseInt(getAppSetting('REACT_APP_SEARCH_DEBOUNCE_IN_MS'))),
    []
  );

  const handleAddUsers = ({ users: selectedUsers }: { users: any[] }) => {
    const newUsers = selectedUsers.filter(
      (u) =>
        !existingUsers.length || !existingUsers.some((eu) => eu.id === u.id)
    );

    if (newUsers.length) {
      setAddedUsers(newUsers);
      updateData(newUsers);
    } else {
      setAddedUsers([]);
      updateData([]);
    }
  };

  const handleRemoveUser = (userId: string) => {
    let newItems = addedUsers.filter((item) => item.id !== userId);
    setAddedUsers(newItems);
    updateData(newItems);
  };

  useEffect(() => {
    if (searchQuery.length) triggerSearch(searchQuery);
  }, [searchQuery]);

  const searchResultsRenderer = ({
    dataSet,
    itemsSelected,
    labels,
    setItemsSelected
  }) =>
    dataSet.map((filteredItem, key) => {
      const itemSelected = !!itemsSelected.users.find(
        (p) => p.id === filteredItem.id
      );
      return (
        <UserWrapper
          {...{
            key,
            itemSelected: itemSelected || filteredItem.isAlreadyAdded,
            onClick: !filteredItem.isAlreadyAdded
              ? () =>
                  onClickSearchResults(
                    filteredItem,
                    itemsSelected,
                    setItemsSelected
                  )
              : undefined
          }}
        >
          <UserInfo
            {...filteredItem}
            email={filteredItem.infoText ?? filteredItem.email}
            size={'small'}
            image={filteredItem.profilePicture}
          />
          <Flexbox>
            {filteredItem.isAlreadyAdded && (
              <ToolButton type="button" onClick={() => ({})} disabled={true}>
                {check}
                <span>{labels.added}</span>
              </ToolButton>
            )}
            {itemSelected && !filteredItem.isAlreadyAdded && (
              <ToolButton type="button" onClick={() => ({})}>
                {check}
                <span>{labels.added}</span>
              </ToolButton>
            )}

            {!itemSelected && !filteredItem.isAlreadyAdded && (
              <ToolButton type="button" onClick={() => ({})}>
                {plus}
                <span>
                  {filteredItem.infoText ? labels.inviteUsers : labels.add}
                </span>
              </ToolButton>
            )}
          </Flexbox>
        </UserWrapper>
      );
    });

  const onClickSearchResults = (
    filteredItem,
    itemsSelected,
    setItemsSelected
  ) => {
    if (itemsSelected.users.find((i) => i.id === filteredItem.id)) {
      setItemsSelected({
        ...itemsSelected,
        users: [...itemsSelected.users.filter((i) => i.id !== filteredItem.id)]
      });
    } else {
      setItemsSelected({
        ...itemsSelected,
        users: [...itemsSelected.users, filteredItem]
      });
    }
  };

  return (
    <>
      <AddUsersHeader>
        To add users use the search form to find the right user to invite.
        Search by name, company or e-mail address.
      </AddUsersHeader>
      <SearchBox
        placeholder={'Type to search'}
        suggestionsPosition={'bottom-start'}
        onValueChange={(value) => {
          setSearchQuery(value);
        }}
        showSuggestions={true}
        label={'Search for users'}
        labels={{ close: 'Close' }}
        onSearch={null}
        autoComplete={'off'}
        valueItalic={false}
        withButton={false}
        icon={search}
        required={false}
        error={false}
        value={searchQuery}
        triggerOnClick={false}
        hasBottomSection={!loading && !!searchTerm.length}
      >
        {loading || !searchTerm.length ? (
          <LoadingWrapper id="mywrapper">
            <LoadingSpinner />
          </LoadingWrapper>
        ) : (
          <>
            {searchResults.length === 0 && (
              <NoResultsFound>
                <p>No results found</p>
              </NoResultsFound>
            )}
            <NextSearchBoxContent
              {...{
                labels: {
                  addUser: 'Add',
                  added: 'Added',
                  add: 'Add'
                },
                loading: false,
                dataSet: searchResults,
                groupData: [],
                setItemsSelected: handleAddUsers,
                searchQuery,
                itemsSelected: {
                  users: [...existingUsers, ...addedUsers]
                },
                canSelectMoreThanOne: true,
                showGroupHeading: true,
                isFetchingMore: false,
                hasFetchMore: false,
                hasInviteUsers: false,
                customResultHandler: searchResultsRenderer
              }}
            />
          </>
        )}
      </SearchBox>
      <UserChipListHeader>Users</UserChipListHeader>
      {!addedUsers.length && (
        <NoUsersAddedInfo>No users have been added yet.</NoUsersAddedInfo>
      )}
      <UserChipList flexWrap="wrap">
        {addedUsers.map((user) => (
          <SelectedUserChip
            key={user.id}
            size="extraSmall"
            onClose={(e) => handleRemoveUser(user.id)}
            userImage={user?.profilePicture}
            {...user}
            fullName={!!user.fullName ? user.fullName : user.email}
          />
        ))}
      </UserChipList>
    </>
  );
};
